<template>
  <div
    :id="`comment-form-${parentId}`"
    ref="commentFormContainer"
    class="group -full -bg-01 -only -stack-space-single"
  >
    <comment-loader v-if="makingApiCall && isEditing" ref="commentFormEditLoader" />

    <div v-show="!makingApiCall">
      <div class="group -flex" :class="{ '-end': isEditing, '-between': !isEditing }">
        <comment-user-name v-if="!isEditing" :user="user" />
        <div class="comment-form-close">
          <material-icon-button
            icon="close"
            classes="-ico-only -no-container"
            aria-label="Close Form"
            @click-action="$emit('close-comment-form')"
          />
        </div>
      </div>

      <div class="post-comment">
        <form id="comment-form" :action="httpMethod" class="comment-form" @submit.prevent="submit">
          <div class="comment-text">
            <form-group input-id="comment">
              <textarea
                ref="textarea"
                v-model="formData.comment"
                :style="textareaStyles"
                rows="5"
                name="comment"
                placeholder="Type your comment."
                data-vv-as="Comment"
                aria-label="Comment"
                :aria-invalid="!!errors.first('comment')"
              />
            </form-group>
          </div>

          {{/* TODO: make file upload component in future */}}
          <div class="form__group" :class="{ error: errors.has(imageIdName) }">
            <div class="comment-image">
              <div class="file-input">
                <label v-if="!hasValidImage" :for="imageIdName" class="image-label flex -vs-center">
                  <span class="image-ico">
                    <i class="material-icons-outlined">image</i>
                  </span>
                  <span class="upload-inst">
                    <span class="image-title">Add an Image</span>
                    <span class="image-limit">10MB Limit</span>
                  </span>
                </label>
                <input
                  :id="imageIdName"
                  ref="fileInput"
                  v-validate="`mimes:${mimeTypes.join(',')}|size:20480`"
                  class="form-control"
                  :name="imageIdName"
                  type="file"
                  :accept="mimeTypes.join(',')"
                  data-vv-as="Image"
                  data-vv-validate-on="change|submit"
                  aria-required="false"
                  :aria-invalid="!!errors.first(imageIdName)"
                  @change="onFileChange"
                />
                <div v-if="hasValidImage" class="file-remove">
                  <button
                    class="btn -image-remove"
                    title="Remove Image"
                    @click.prevent="removeFile"
                  >
                    <span class="iconfont-menu-close" />Remove Image
                  </button>
                  <img :src="image" alt="" />
                </div>
              </div>
            </div>
            <span v-if="errors.has(imageIdName)" class="message">{{
              errors.first(imageIdName)
            }}</span>
          </div>
          <div class="comment-action">
            <button v-if="!isEditing" class="btn -dark" @click.prevent="submit">Post</button>
            <button v-if="isEditing" class="btn -dark" @click.prevent="submit">Save</button>
          </div>
        </form>
        <div class="discussion-policy">
          <a href="/page/discussion-policy" class="">Discussion Policy</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// @flow
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useCommentsStore } from '@/stores/comments'
import CommentLoader from './comment-loader.vue'
import CommentUserName from './comment-user-name.vue'
import FormGroup from '@/components/forms/form-group.vue'
import MaterialIconButton from '@/components/buttons/material-icon-button.vue'
import { scrollToElemTop } from '@/utils/scroll-to'
import { fbAPIPromise } from '@/utils/http-api'
import { buildComment } from '@/utils/comments'

export default {
  components: { MaterialIconButton, CommentLoader, CommentUserName, FormGroup },
  props: {
    parentId: {
      type: Number,
      default: -1,
    },
    comment: {
      type: Object,
      default: () => {},
    },
    commentableId: {
      type: Number,
      required: true,
    },
    commentableType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // form processing state
      processing: false,
      removeImage: 0,
      // the actual image file as a string, after the file is selected
      // a string to display in img src
      imageDataString: '',
      textareaStyles: 'overflow-wrap: break-word; resize: vertical;',
      mimeTypes: ['image/png', 'image/jpeg', 'image/gif'],
      makingApiCall: false,
      formData: {
        // defaults
        id: this.comment && this.comment.id ? this.comment.id : -1,
        comment: this.comment && this.comment.comment ? this.comment.comment : '',
        commentableType: this.commentableType,
        commentableId: this.commentableId,
        parentId: this.parentId,
        removeImage: 0,
        image: null,
      },
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['user']),

    isEditing() {
      return this.formData.id !== -1
    },
    imageIdName() {
      return this.formData.id <= 0
        ? `image-field-${this.formData.parentId}`
        : `image-field-${this.formData.id}`
    },
    image() {
      // if user removing image, never show this.comment?.imageUrl
      // only file input images
      if (this.formData.removeImage) {
        return this.imageDataString || ''
      }

      return this.imageDataString || this.comment?.imageUrl
    },
    hasValidImage() {
      return !this.errors.has(this.imageIdName) && this.image
    },
    httpMethod() {
      return this.isEditing ? 'put' : 'post'
    },
  },
  created() {
    // custom validation messages for comment images
    this.$validator.updateDictionary({
      en: {
        messages: {
          mimes: `Only ${this.mimeTypes.join(', ').replace(/image\//g, '')} supported`,
          size: 'Must be an image under 10MB',
        },
      },
    })
  },

  mounted() {
    // smooth scroll and focus the form when it is opened
    this.focusForm()
  },

  methods: {
    ...mapActions(useCommentsStore, ['increaseCommentsTotal', 'decreaseCommentsTotal']),

    // scrolls to get the form into view and then focuses on the textarea input
    focusForm() {
      this.$nextTick(() => {
        const commentFormContainer = this.$refs.commentFormContainer

        if (!commentFormContainer) {
          return
        }

        // scroll
        const animationTime = 350
        scrollToElemTop({
          elem: commentFormContainer,
          ms: animationTime,
        })

        // focus
        if (this.$refs.textarea) {
          setTimeout(() => {
            this.$refs.textarea.focus()
          }, animationTime)
        }
      })
    },

    async submit() {
      if (this.makingApiCall) {
        return
      }

      this.makingApiCall = true

      try {
        //must have comment OR image OR both to proceed
        if (!this.formData.comment.trim() && !this.formData.image) {
          this.$validator.errorBag.add('comment', 'Comment or image required')
          this.makingApiCall = false
          return
        }

        // remove comment / image error
        this.$validator.errorBag.remove('comment')

        // any other validation errors
        const result = await this.$validator.validateAll()

        // success
        if (result) {
          if (!this.isEditing) {
            this.$emit('toggle-loading', {
              loading: true,
              parentId: this.formData.parentId,
              amount: 1,
            })
          }

          if (!this.isEditing) {
            this.increaseCommentsTotal()
          }

          // scroll to the loader where the comment will show up
          this.$nextTick(() => {
            const loader = this.$refs.commentLoader
            if (loader) {
              const animationTime = 500
              scrollToElemTop({
                elem: loader,
                ms: animationTime,
              })
            }
          })

          const formData = new window.FormData()
          formData.append('commentable_type', this.formData.commentableType)
          formData.append('commentable_id', this.formData.commentableId)
          formData.append('comment', this.formData.comment)
          if (this.formData.parentId > 0) {
            formData.append('parent_id', this.formData.parentId)
          }
          if (this.formData.image) {
            formData.append('image', this.formData.image)
          }
          if (this.formData.removeImage) {
            formData.append('image_remove', this.formData.removeImage)
          }

          // have to do this because image upload requires formData
          // and spoofing laravel http method to put
          if (this.isEditing) {
            formData.append('_method', this.httpMethod)
          }

          try {
            let url = this.isEditing ? `/comments/${this.formData.id}` : '/comments'

            // api call
            const res = await fbAPIPromise({
              callType: 'post',
              url: url,
              body: formData,
            })

            if (!res || !res.body || !res.body.data) {
              throw new Error(`Unable to submit comment, please try again`)
            }

            const newComment = buildComment(res.body.data)

            if (this.isEditing) {
              this.$emit('edit-comment', newComment)
            } else {
              this.$emit('add-comment', newComment)
            }

            this.$emit('close-comment-form')
          } catch (err) {
            if (!this.isEditing) {
              this.decreaseCommentsTotal()
            }
          } finally {
            this.$emit('toggle-loading', { loading: false, parentId: -1, amount: 0 })
          }
        }
      } catch (err) {
        // error handling
      } finally {
        this.makingApiCall = false
      }
    },

    onFileChange(e: any) {
      const target = e.target || e.dataTransfer
      const files = target && target.files

      if (!files.length) {
        return
      }

      // do not render if not supported image
      if (!this.mimeTypes.includes(files[0].type)) {
        return
      }

      this.createImage(files[0])
    },

    // display image inline ONLY if no error
    createImage(file: any) {
      this.formData.image = file

      const reader = new window.FileReader()

      reader.onload = (e) => {
        this.imageDataString = e.target.result
      }

      reader.readAsDataURL(file)
    },

    removeFile() {
      this.$refs.fileInput.value = ''
      this.imageDataString = ''
      this.formData.image = ''

      // set a flag to tell server to remove the file.  Only do if comment on server has image
      if (this.image) {
        this.formData.removeImage = 1
      }
    },
  },
}
</script>
